import React, { useState, useEffect } from 'react';
import { useForm } from '../../../context/FormContext';
import FormField from '../../common/FormField/FormField';
import { validateSection, isSectionComplete } from '../../../utils/validation';
import './BusinessPresentation.css';

const BusinessPresentation = ({ data, errors, onSave, isLoading, isReadOnly }) => {
  const { actions } = useForm();
  const [formData, setFormData] = useState(data);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleChange = (field, value) => {
    if (isReadOnly) return;

    const newData = { ...formData, [field]: value };
    setFormData(newData);
    setHasChanges(true);

    actions.updateSectionData('businessPresentation', newData, 'in_progress');
  };

  const handleSave = () => {
    if (hasChanges) {
      // Validate the section
      const validationErrors = validateSection('businessPresentation', formData);
      const isComplete = isSectionComplete('businessPresentation', formData);

      // Set validation errors in context
      actions.setSectionErrors('businessPresentation', validationErrors);

      // Determine status based on completion
      const status = isComplete ? 'completed' : 'in_progress';

      // Update section data with proper status
      actions.updateSectionData('businessPresentation', formData, status);

      // Call the save handler
      onSave(formData);
      setHasChanges(false);
    }
  };

  // Removed auto-saving - users must manually save sections

  return (
    <div className="business-presentation">
      <div className="section-intro">
        <p>Please provide detailed information about your business operations.</p>
      </div>

      <div className="form-grid">
        <FormField
          label="Business activity description"
          required
          error={errors.businessDescription}
          className="grid-span-2"
        >
          <textarea
            className={`form-textarea ${errors.businessDescription ? 'error' : ''}`}
            value={formData.businessDescription || ''}
            onChange={(e) => handleChange('businessDescription', e.target.value)}
            placeholder="Describe your business activities in detail"
            rows={4}
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Payment flows explanation"
          required
          error={errors.paymentFlows}
          className="grid-span-2"
        >
          <textarea
            className={`form-textarea ${errors.paymentFlows ? 'error' : ''}`}
            value={formData.paymentFlows || ''}
            onChange={(e) => handleChange('paymentFlows', e.target.value)}
            placeholder="Explain your payment flows and processes"
            rows={4}
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Geographic distribution of business"
          required
          error={errors.geographicDistribution}
          className="grid-span-2"
        >
          <textarea
            className={`form-textarea ${errors.geographicDistribution ? 'error' : ''}`}
            value={formData.geographicDistribution || ''}
            onChange={(e) => handleChange('geographicDistribution', e.target.value)}
            placeholder="Describe the geographic distribution of your business"
            rows={3}
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Customer types and percentages"
          required
          error={errors.customerTypes}
          className="grid-span-2"
        >
          <textarea
            className={`form-textarea ${errors.customerTypes ? 'error' : ''}`}
            value={formData.customerTypes || ''}
            onChange={(e) => handleChange('customerTypes', e.target.value)}
            placeholder="Describe your customer types and their percentages"
            rows={3}
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Cryptocurrency experience"
          required
          error={errors.cryptoExperience}
          className="grid-span-2"
        >
          <textarea
            className={`form-textarea ${errors.cryptoExperience ? 'error' : ''}`}
            value={formData.cryptoExperience || ''}
            onChange={(e) => handleChange('cryptoExperience', e.target.value)}
            placeholder="Describe your experience with cryptocurrency"
            rows={3}
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Risk assessment information"
          required
          error={errors.riskAssessment}
          className="grid-span-2"
        >
          <textarea
            className={`form-textarea ${errors.riskAssessment ? 'error' : ''}`}
            value={formData.riskAssessment || ''}
            onChange={(e) => handleChange('riskAssessment', e.target.value)}
            placeholder="Provide risk assessment information"
            rows={4}
            disabled={isReadOnly}
          />
        </FormField>
      </div>

      {!isReadOnly && (
        <div className="section-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Section'}
          </button>

          {hasChanges && (
            <span className="unsaved-indicator">
              You have unsaved changes
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessPresentation;
